import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Star } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const collections = [
  {
    title: 'Temple Necklaces',
    description: 'Layered silhouettes with ceremonial presence, sculpted to feel rich and enduring.',
    image:
      'https://static.prod-images.emergentagent.com/jobs/8a6b80b4-20bf-4817-ba53-92ca54091425/images/5925c218e24e35a19143d615e0882214c4c3c6341b529dd9426f1aa84609e826.png',
    link: '/products?category=Necklaces'
  },
  {
    title: 'Signature Earrings',
    description: 'Refined detailing and warm antique tones designed to frame every occasion gracefully.',
    image:
      'https://static.prod-images.emergentagent.com/jobs/8a6b80b4-20bf-4817-ba53-92ca54091425/images/5d6acac1a7c2f1d1b7b20d7a3e6ed06c727aa161e900460d3d5e44eda208db51.png',
    link: '/products?category=Earrings'
  },
  {
    title: 'Bridal Treasury',
    description: 'Statement heirlooms curated for weddings, rituals, and unforgettable family moments.',
    image:
      'https://images.unsplash.com/photo-1770838446917-cf324f1be078?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxzb3V0aCUyMGluZGlhbiUyMGdvbGQlMjBqZXdlbHJ5fGVufDB8fHx8MTc3NTU1MDMzOHww&ixlib=rb-4.1.0&q=85',
    link: '/products?category=Bridal Collection'
  }
];

const heritageLinks = ['The Treasury', 'Bridal Chronicle', 'House of Craft', 'Our Heritage', 'Salon of Jewels'];

const Home = () => {
  const navigate = useNavigate();
  const handleAboutNavigation = useCallback(() => {
    navigate('/about');
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f4efe7]">
      <Header />

      <main className="flex-1">
        <div className="w-full overflow-hidden border-y border-[#ccb693] bg-[#f7f1e6] shadow-[0_24px_80px_rgba(58,40,20,0.18)]">
          <section
            className="relative min-h-[720px] overflow-hidden px-6 py-10 sm:px-10 lg:px-16"
            style={{
              backgroundImage:
                "linear-gradient(rgba(22,11,8,0.52), rgba(22,11,8,0.52)), url('/2.jpeg')",
              backgroundSize: 'cover',
              backgroundPosition: 'center top'
            }}
            data-testid="hero-section"
          >
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(28,14,10,0.08),rgba(24,12,9,0.68))]"></div>
            <div className="absolute inset-0 opacity-40 vintage-grain"></div>
            <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_50%_26%,rgba(238,192,105,0.14),transparent_18%),linear-gradient(90deg,rgba(247,241,230,0.08),transparent_22%,transparent_78%,rgba(247,241,230,0.06))]"></div>

            <div className="relative z-10 mx-auto flex min-h-[640px] w-full max-w-7xl flex-col">
              <div className="flex flex-1 items-center justify-center">
                <div className="max-w-2xl text-center text-[#f6ecdd]">
                  <p className="mt-10 text-sm font-semibold uppercase tracking-[0.18em] text-[#d8b266] sm:text-base">
                    A Timeless South Indian Jewelry House
                  </p>
                  <p className="mx-auto mt-3 max-w-xl text-base italic leading-7 text-[#f0e2cd] sm:text-lg">
                    "Adornment becomes more captivating when it carries memory, craftsmanship, and a sense of occasion."
                  </p>

                  <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                    <Link to="/products">
                      <Button
                        className="theme-primary premium-button rounded-none border border-[#d7b97a] px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em]"
                        data-testid="explore-collection-btn"
                      >
                        Explore Collection <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/about">
                      <Button
                        variant="outline"
                        className="rounded-none border border-[#e8d6b7] bg-[rgba(255,247,234,0.12)] px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#fff5e5] transition-colors hover:bg-[#f3e7d2] hover:text-[#4d2a1f]"
                        onClick={handleAboutNavigation}
                      >
                        Learn Our Story
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="border-y border-[#c9a45b] bg-[#261a16] px-5 py-3 text-[#e3c57b] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
            <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-3 text-[11px] uppercase tracking-[0.18em] sm:gap-5">
              {heritageLinks.map((item, index) => (
                <React.Fragment key={item}>
                  <span>{item}</span>
                  {index < heritageLinks.length - 1 && <Star className="h-3 w-3 fill-current" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <section className="relative overflow-hidden border-t border-[#e4d8c3] bg-[#eee6d8] px-6 py-14 sm:px-10">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/paper-fibers.png')] opacity-20"></div>
            <div className="absolute left-0 top-0 h-full w-1/3 bg-[linear-gradient(90deg,rgba(65,48,38,0.14),transparent)]"></div>
            <div className="absolute right-0 top-0 h-full w-1/3 bg-[linear-gradient(270deg,rgba(65,48,38,0.14),transparent)]"></div>

            <div className="relative mx-auto max-w-7xl text-center text-[#3c2a22]">
              <p className="text-xs uppercase tracking-[0.28em] text-[#8e755d]">About Us</p>
              <h2 className="mt-3 text-4xl font-heading italic sm:text-5xl">Panchami</h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm uppercase tracking-[0.18em] text-[#6d5647]">
                A historical expression of South Indian tradition and modern grace
              </p>
              <p className="mx-auto mt-6 max-w-3xl text-sm leading-7 text-[#554137] sm:text-base">
                Panchami presents jewelry with the warmth of a family treasury and the polish of a refined boutique.
                Our collections celebrate temple artistry, bridal grandeur, and everyday elegance through pieces chosen
                to feel storied, luminous, and enduring.
              </p>
            </div>
          </section>

          <section className="bg-[#f7f1e6] px-6 py-14 sm:px-10">
            <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-3">
              {collections.map((item) => (
                <article key={item.title} className="text-center text-[#35261f]">
                  <Link to={item.link} className="group block">
                    <div className="mx-auto max-w-[240px] border border-[#d8c4a3] bg-[#f1e8d8] p-3 shadow-[0_12px_25px_rgba(86,64,34,0.12)] transition-transform duration-300 group-hover:-translate-y-1">
                      <div className="aspect-[4/3] overflow-hidden border border-[#cab28d]">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="h-full w-full object-cover sepia-[0.28] contrast-[0.95] transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    </div>
                  </Link>

                  <h3 className="mt-6 text-2xl font-heading font-semibold uppercase tracking-[0.04em]">
                    {item.title}
                  </h3>
                  <p className="mx-auto mt-3 max-w-[250px] text-sm leading-6 text-[#5d483d]">
                    {item.description}
                  </p>
                  <Link to={item.link}>
                    <Button className="mt-6 rounded-none border border-[#c9a45b] bg-[#c79a35] px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#b78922]">
                      Read More
                    </Button>
                  </Link>
                </article>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
